import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Tabs as TabsPrimitive } from "radix-ui"

import { cn } from "@/shared/lib/utils"
import { TabHint } from "@/shared/components/ui/tag"

function Tabs({
  className,
  orientation = "horizontal",
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      data-orientation={orientation}
      className={cn(
        "group/tabs flex gap-2 data-horizontal:flex-col",
        className
      )}
      {...props}
    />
  )
}

const tabsListVariants = cva(
  "group/tabs-list inline-flex w-fit items-center justify-center rounded-full p-1 text-muted-foreground group-data-horizontal/tabs:h-auto group-data-vertical/tabs:h-fit group-data-vertical/tabs:flex-col data-[variant=line]:rounded-none data-[variant=line]:bg-transparent data-[variant=line]:p-0",
  {
    variants: {
      variant: {
        default: "bg-muted",
        line: "gap-1 bg-transparent",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

/** 分段胶囊 TabsList 常用扩展（换行、全宽容器内左对齐） */
export const segmentedTabsListClassName =
  "h-auto max-w-full flex-wrap justify-start"

/** 分段胶囊 TabsTrigger 尺寸 */
export const segmentedTabsTriggerVariants = cva("group/trigger", {
  variants: {
    size: {
      xs: "rounded-full px-2.5 py-1 text-xs",
      sm: "rounded-full px-3 py-1.5 text-xs sm:text-sm",
      md: "rounded-full px-3.5 py-2 text-sm",
    },
  },
  defaultVariants: {
    size: "sm",
  },
})

function TabsList({
  className,
  variant = "default",
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List> &
  VariantProps<typeof tabsListVariants>) {
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      data-variant={variant}
      className={cn(tabsListVariants({ variant }), className)}
      {...props}
    />
  )
}

function TabsTrigger({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(
        "relative inline-flex h-[calc(100%-1px)] flex-1 items-center justify-center gap-1.5 rounded-md border border-transparent px-1.5 py-0.5 text-sm font-normal whitespace-nowrap text-muted-foreground transition-all group-data-vertical/tabs:w-full group-data-vertical/tabs:justify-start hover:text-foreground/80 focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-1 focus-visible:outline-ring disabled:pointer-events-none disabled:opacity-50 has-data-[icon=inline-end]:pr-1 has-data-[icon=inline-start]:pl-1 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        "group-data-[variant=default]/tabs-list:rounded-full",
        "data-[state=active]:bg-background data-[state=active]:font-medium data-[state=active]:text-foreground data-[state=active]:shadow-sm dark:data-[state=active]:bg-input/30",
        "group-data-[variant=line]/tabs-list:rounded-none group-data-[variant=line]/tabs-list:bg-transparent group-data-[variant=line]/tabs-list:font-medium group-data-[variant=line]/tabs-list:text-foreground/60 group-data-[variant=line]/tabs-list:hover:text-foreground group-data-[variant=line]/tabs-list:data-[state=active]:bg-transparent group-data-[variant=line]/tabs-list:data-[state=active]:text-foreground group-data-[variant=line]/tabs-list:data-[state=active]:shadow-none",
        "after:absolute after:bg-foreground after:opacity-0 after:transition-opacity group-data-horizontal/tabs:after:inset-x-0 group-data-horizontal/tabs:after:bottom-[-5px] group-data-horizontal/tabs:after:h-0.5 group-data-vertical/tabs:after:inset-y-0 group-data-vertical/tabs:after:-right-1 group-data-vertical/tabs:after:w-0.5 group-data-[variant=line]/tabs-list:data-[state=active]:after:opacity-100",
        className
      )}
      {...props}
    />
  )
}

function TabsContent({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn("flex-1 text-sm outline-none", className)}
      {...props}
    />
  )
}

export type SegmentedTabsOption<T extends string = string> = {
  value: T
  label: React.ReactNode
  /** 内联角标，渲染为 [`TabHint`](./tag.tsx) */
  hint?: React.ReactNode
}

type SegmentedTabsProps<T extends string> = {
  options: SegmentedTabsOption<T>[]
  value: T
  onChange: (value: T) => void
  className?: string
  listClassName?: string
  triggerClassName?: string
  size?: NonNullable<VariantProps<typeof segmentedTabsTriggerVariants>["size"]>
  testIdPrefix?: string
  "aria-label"?: string
}

/** 受控分段胶囊 Tabs — 列表筛选 / 页内切换等统一入口 */
function SegmentedTabs<T extends string>({
  options,
  value,
  onChange,
  className,
  listClassName,
  triggerClassName,
  size = "sm",
  testIdPrefix,
  "aria-label": ariaLabel,
}: SegmentedTabsProps<T>) {
  return (
    <Tabs
      value={value}
      onValueChange={(next) => onChange(next as T)}
      className={cn("gap-0", className)}
      aria-label={ariaLabel}
    >
      <TabsList className={cn(segmentedTabsListClassName, listClassName)}>
        {options.map((option) => (
          <TabsTrigger
            key={option.value}
            value={option.value}
            className={cn(segmentedTabsTriggerVariants({ size }), triggerClassName)}
            data-testid={testIdPrefix ? `${testIdPrefix}-${option.value}` : undefined}
          >
            {option.label}
            {option.hint ? <TabHint>{option.hint}</TabHint> : null}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  )
}

export {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  tabsListVariants,
  SegmentedTabs,
}
